import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { hasPermission } from '@/config/menu';
import { selectCurrentUserPermissions } from '@/redux/slices/authSlice';

/**
 * F-9 — shared permission hook for app views. Replaces the ad-hoc
 * `currentUserPermissions?.includes(...)` checks scattered across pages
 * with the same semantics the Sidebar's menu filtering uses.
 *
 * @example
 * const { can, canAny } = usePermission();
 * can(Permission.PAYROLL_SENSITIVE_VIEW); // single permission
 * canAny([Permission.SALARY_SETUP_VIEW, Permission.PAYROLL_RUN_VIEW]); // any of
 */
export function usePermission() {
  const permissions = useSelector(selectCurrentUserPermissions);

  const can = useCallback(
    (permission: string | undefined | null): boolean =>
      permission ? hasPermission(permissions, [permission]) : false,
    [permissions],
  );

  const canAny = useCallback(
    (required: Array<string | undefined | null>): boolean => {
      const list = required.filter((p): p is string => typeof p === 'string' && p.length > 0);

      // Fail closed, consistently with `canAll`: `hasPermission` returns true
      // for an empty list (menu-filtering semantics), which would silently
      // grant access to callers passing a dynamically-built empty requirement.
      return list.length > 0 && hasPermission(permissions, list);
    },
    [permissions],
  );

  const canAll = useCallback(
    (required: Array<string | undefined | null>): boolean => {
      const list = required.filter((p): p is string => typeof p === 'string' && p.length > 0);

      return list.length > 0 && list.every((p) => hasPermission(permissions, [p]));
    },
    [permissions],
  );

  return { permissions, can, canAny, canAll };
}
